import { useEffect, useState, type FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { TopBarNotificationType } from 'constants/notifications';
import { ThemeName } from 'constants/themes';
import { isMobileViewMemoized } from 'helpers/device';
import { openInCard } from 'helpers/routing';
import { useFetchFeatures } from 'hooks/api/feature-control/use-fetch-features';
import { useProductData } from 'hooks/api/use-product-data';
import { useWindowSize } from 'hooks/use-window-size';
import { FeatureNames } from 'services/api/feature-control/interfaces';
import { getCurrentTheme, hasFetchedAgentCustomProperties } from 'store/features/agent-custom-properties/selectors';
import { getOpenedGlobalModal } from 'store/features/global-modals/selectors';
import { NotificationsBarActions } from 'store/features/notifications-bar/actions';

import { getLegacyMigrationLandingUrl, getLegacyMigrationModalAssets, hasLegacyMigrationProducts } from './helpers';
import { LegacyMigrationModal } from './LegacyMigrationModal';

export const LegacyMigrationCampaign: FC = () => {
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();
  const { data: features, isFetched: areFeaturesFetched } = useFetchFeatures();
  const { installedProducts, areProductsFetched } = useProductData();
  const areAgentPropertiesFetched = useSelector(hasFetchedAgentCustomProperties);
  const currentTheme = useSelector(getCurrentTheme);
  const openedGlobalModal = useSelector(getOpenedGlobalModal);
  const [isModalDismissedInSession, setIsModalDismissedInSession] = useState(false);
  const isReady = areFeaturesFetched && areProductsFetched && areAgentPropertiesFetched;
  const shouldShowCampaign =
    isReady &&
    features[FeatureNames.LegacyProductsMigrationCampaign] &&
    hasLegacyMigrationProducts(installedProducts) &&
    !isMobileViewMemoized(windowWidth);
  const shouldShowModal = shouldShowCampaign && !openedGlobalModal && !isModalDismissedInSession;
  const shouldShowTopbar = shouldShowCampaign;

  useEffect(() => {
    const action = shouldShowTopbar
      ? NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.LegacyMigration })
      : NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.LegacyMigration);

    dispatch(action);
  }, [dispatch, shouldShowTopbar]);

  useEffect(
    () => () => {
      dispatch(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.LegacyMigration));
    },
    [dispatch],
  );

  if (!shouldShowModal) {
    return null;
  }

  const handleClose = (): void => {
    setIsModalDismissedInSession(true);
  };

  const handlePrimaryAction = (): void => {
    setIsModalDismissedInSession(true);
    openInCard(getLegacyMigrationLandingUrl());
  };

  const assets = getLegacyMigrationModalAssets();
  const imageSource = currentTheme === ThemeName.Dark ? assets.dark : assets.light;

  return <LegacyMigrationModal imageSource={imageSource} onClose={handleClose} onPrimaryAction={handlePrimaryAction} />;
};
